import axios from 'axios';

// Always use same-origin relative URLs
// Both preview and production route /api/* to backend via reverse proxy
// This avoids cross-domain issues (ISP blocking of emergent.host in Turkey)
const API_URL = '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateTheme: (theme) => api.put('/auth/theme', { theme }),
};

// Companies
export const companiesAPI = {
  getAll: () => api.get('/companies'),
  getOne: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
};

// Incomes
export const incomesAPI = {
  getAll: (companyId) => api.get('/incomes', { params: { company_id: companyId } }),
  create: (data) => api.post('/incomes', data),
  delete: (id) => api.delete(`/incomes/${id}`),
};

// Expenses
export const expensesAPI = {
  getAll: (companyId, category) => api.get('/expenses', { params: { company_id: companyId, category } }),
  create: (data) => api.post('/expenses', data),
  delete: (id) => api.delete(`/expenses/${id}`),
};

// Personnel
export const personnelAPI = {
  getAll: (companyId) => api.get('/personnel', { params: { company_id: companyId } }),
  getOne: (id) => api.get(`/personnel/${id}`),
  create: (data) => api.post('/personnel', data),
  update: (id, data) => api.put(`/personnel/${id}`, data),
  delete: (id) => api.delete(`/personnel/${id}`),
};

// Subscriptions
export const subscriptionsAPI = {
  getAll: () => api.get('/subscriptions'),
  create: (data) => api.post('/subscriptions', data),
  delete: (id) => api.delete(`/subscriptions/${id}`),
};

// Wallets
export const walletsAPI = {
  getAll: () => api.get('/wallets'),
  create: (data) => api.post('/wallets', data),
  delete: (id) => api.delete(`/wallets/${id}`),
};

// Transactions
export const transactionsAPI = {
  getAll: () => api.get('/transactions'),
  create: (data) => api.post('/transactions', data),
};

// Dashboard & Reports
export const dashboardAPI = {
  getSummary: () => api.get('/dashboard/summary'),
  getCompanyComparison: () => api.get('/reports/companies'),
  getExpensesByCategory: (companyId) => api.get('/reports/expenses-by-category', { params: { company_id: companyId } }),
};

// Partners
export const partnersAPI = {
  getAll: (companyId) => api.get('/partners', { params: { company_id: companyId } }),
  create: (data) => api.post('/partners', data),
  update: (id, data) => api.put(`/partners/${id}`, data),
  delete: (id) => api.delete(`/partners/${id}`),
};

// Affiliate Companies
export const affiliateCompaniesAPI = {
  getAll: () => api.get('/affiliate-companies'),
  getOne: (id) => api.get(`/affiliate-companies/${id}`),
  create: (data) => api.post('/affiliate-companies', data),
  update: (id, data) => api.put(`/affiliate-companies/${id}`, data),
  delete: (id) => api.delete(`/affiliate-companies/${id}`),
};

// Affiliate Brands
export const affiliateBrandsAPI = {
  getAll: (companyId) => api.get('/affiliate-brands', { params: { company_id: companyId } }),
  create: (data) => api.post('/affiliate-brands', data),
  update: (id, data) => api.put(`/affiliate-brands/${id}`, data),
  delete: (id) => api.delete(`/affiliate-brands/${id}`),
  getCredentials: (id) => api.get(`/affiliate-brands/${id}/credentials`),
  getPredefined: () => api.get('/predefined-brands'),
};

// Affiliate Stats
export const affiliateStatsAPI = {
  getAll: (brandId, periodType) => api.get('/affiliate-stats', { params: { brand_id: brandId, period_type: periodType } }),
  create: (data) => api.post('/affiliate-stats', data),
  getSummary: (dateRange) => api.get('/affiliate-stats/summary', { params: { date_range: dateRange } }),
  importCSV: (brandId, fileContent) => api.post('/affiliate-stats/import-csv', { brand_id: brandId, file_content: fileContent }),
};

// Scraping (legacy - kept for log viewing)
export const scrapingAPI = {
  getLogs: (brandId) => api.get('/scrape-logs', { params: { brand_id: brandId } }),
  getLog: (logId) => api.get(`/scrape-logs/${logId}`),
};

// CSV Update Import
export const updateAPI = {
  importCSV: (provider, platformName, fileContent, mediaFileContent = null) => api.post('/update/csv-import', { provider, platform_name: platformName, file_content: fileContent, media_file_content: mediaFileContent }),
  startImportJob: (brandId, fileContent, fileName) => api.post('/update/import-job', { brand_id: brandId, file_content: fileContent, file_name: fileName }),
  getImportJob: (jobId) => api.get(`/update/import-job/${jobId}`),
  getImportStatus: () => api.get('/update/import-status'),
  getImportHistory: () => api.get('/update/import-history'),
};

// Admin User Management
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  updatePermissions: (userId, data) => api.put(`/admin/users/${userId}/permissions`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getPages: () => api.get('/admin/pages'),
  reset2FA: (userId) => api.post(`/admin/users/${userId}/reset-2fa`),
};

export default api;
